import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
  ViewChild,
  AfterViewInit,
  effect,
} from '@angular/core';
import { SetupNodeComponent } from '../../setup-node/setup-node.component';
import { DxTextBoxComponent, DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxValidatorComponent, DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { DxFormComponent, DxiFormItemComponent } from 'devextreme-angular/ui/form';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { DxPopoverComponent } from 'devextreme-angular/ui/popover';
import { Milestone, Phase, TaskPackage } from '../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-task-package-node',
    imports: [
        CommonModule,
        SetupNodeComponent,
        DxValidatorModule,
        DxiValidationRuleComponent,
        SvgIconComponent,
        DxFormComponent,
        DxiFormItemComponent,
        DxPopoverComponent,
        DxTextBoxModule,
    ],
    templateUrl: './setup-task-package-node.component.html',
    styleUrl: './setup-task-package-node.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupTaskPackageNodeComponent implements AfterViewInit {
  // Bind the validation callback in constructor to ensure proper context
  public uniqueNameValidationCallback: (params: any) => boolean;

  taskPackage = model.required<TaskPackage>();
  milestone = model.required<Milestone>();
  phases = model.required<Phase[]>();
  editing = model<boolean>();
  expanded = model<boolean>(true);
  validationGroup = input<string>();
  allowDelete = input<boolean>();
  onDelete = output<TaskPackage>();

  private changes: boolean = false;
  public isValid: boolean = null;
  private callbacks: Array<() => void> = [];

  adapterConfig = {
    getValue: () => this.validateForm(),
    validationRequestsCallbacks: this.callbacks,
  };

  @ViewChild(DxFormComponent)
  form!: DxFormComponent;

  constructor() {
    // Bind the validation callback to ensure proper context
    this.uniqueNameValidationCallback = this.validateUniqueName.bind(this);
    // Watch for changes in phases signal and trigger re-validation
    this.phases.subscribe(() => {
      if (this.form?.instance) {
        this.form.instance.validate();
      }
      this.changes = true; // Force validation callback to re-run
    });
  }

  // Validation callback for the custom validation rule
  validationCallback = () => {
    const isValid = this.validateForm();
    return isValid;
  };

  ngAfterViewInit() {}

  private validateForm(): boolean {
    if (this.form?.instance) {
      const formValidation = this.form.instance.validate();
      const isValid = formValidation?.isValid ?? false;

      // Only trigger callbacks if validation state changed
      if (this.isValid !== isValid) {
        this.isValid = isValid;
        this.revalidate();
      }

      return isValid;
    }
    return false;
  }

  private revalidate() {
    this.callbacks.forEach(func => {
      func();
    });
  }

  onDataChanged() {
    // Trigger validation update when data changes
    this.isValid = null; // Force revalidation
    this.revalidate();
  }

  onDeleteClicked() {
    this.onDelete.emit(this.taskPackage());
  }

  /**
   * Method version of the unique name validation for proper binding
   */
  private validateUniqueName(params: any): boolean {
    const currentName = params?.value || params?.data;

    // If no name entered, let the required validation handle it
    if (!currentName || currentName.trim() === '') {
      return true;
    }

    // Get the current task package to exclude it from duplicate check
    const currentTaskPackage = this.taskPackage();
    const currentMilestone = this.milestone();
    const allPhases = this.phases();

    // Check if any task package in any milestone in any phase has the same name (case-insensitive)
    const duplicateExists = allPhases.some(phase => {
      if (!phase.milestones || phase.milestones.length === 0) {
        return false;
      }

      return phase.milestones.some(milestone => {
        if (!milestone.taskPackages || milestone.taskPackages.length === 0) {
          return false;
        }

        return milestone.taskPackages.some(taskPackage => {
          // Skip the current task package we're editing (using id comparison)
          if (taskPackage.id === currentTaskPackage.id) {
            return false;
          }

          const hasName = taskPackage.name;
          const isSameName = hasName && taskPackage.name.trim().toLowerCase() === currentName.trim().toLowerCase();

          return hasName && isSameName;
        });
      });
    });

    return !duplicateExists;
  }
}
